/**
 * DocumentUploadSection – Standardized Settlement Version
 * Bridges the shared FileUpload UI with Settlement-specific logic.
 */
import { useState } from 'react';
import FileUpload from '../ui/FileUpload';
import { DocumentsIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';


interface DocumentUploadSectionProps {
  documents: AppDocument[];
  onUpload: (files: File[]) => Promise<void>;
  onDelete?: (docId: string) => Promise<void>;
  disabled?: boolean;
  className?: string;
}


export default function DocumentUploadSection({
  documents,
  onUpload,
  onDelete,
  disabled,
  className = '',
}: DocumentUploadSectionProps) {
  const [isUploading, setIsUploading] = useState(false);


  const handleInternalUpload = async (files: File[]) => {
    if (disabled || isUploading || !files.length) return;
   
    setIsUploading(true);
    try {
      // This calls the handler in SFrame124
      await onUpload(files);
    } catch (err) {
      console.error('[DocumentUploadSection] Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };


  return (
    <div className={`space-y-6 ${className}`}>
      <div>
        <label className="block text-sm font-semibold text-stone-800 uppercase tracking-wide mb-3">
          Documents
        </label>
       
        {/* The shared UI component from the Planning side */}
        <FileUpload
          onUpload={handleInternalUpload}
          accept=".pdf,.jpg,.png,.docx"
          multiple
        />


        {isUploading && (
          <div className="mt-3 text-sm text-cyan-600 flex items-center gap-2 animate-pulse">
            <div className="w-3 h-3 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin" />
            <span>Uploading to secure storage...</span>
          </div>
        )}


        {/* The list showing files instantly */}
        <div className="space-y-2 mt-4">
          {documents && documents.length > 0 ? (
            documents.map((doc, index) => {
              const isTemp = doc.id?.toString().startsWith('temp-');
             
              return (
                <div
                  key={doc.id || `doc-${index}`}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-white border rounded-md gap-3 shadow-sm transition-all ${
                    isTemp ? 'border-cyan-200 bg-cyan-50/30 italic' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left">
                    <DocumentsIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isTemp ? 'text-cyan-400' : 'text-cyan-700'}`} />
                    <span className="text-sm text-gray-700 truncate font-medium" title={doc.name}>
                      {doc.name}
                    </span>
                  </div>


                  <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                    {!isTemp && (
                      <button
                        type="button"
                        onClick={() => documentService.open(doc.id)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium px-2 py-1 rounded hover:bg-cyan-50"
                      >
                        Preview
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(doc.id)}
                        className="text-red-500 hover:text-red-700 text-sm font-semibold ml-4 px-2 py-1 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            !isUploading && (
              <div className="text-center py-6 border-2 border-dashed border-stone-100 rounded-xl">
                <p className="text-sm text-stone-400 font-medium">No documents attached yet.</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
